import { makeAutoObservable } from 'mobx';
import { message } from 'antd';
import { AttachmentType } from '../config';

export class OperationsManager {
    _libState = null;
    _isSaving = false;
    _intervalId = null; // Declare intervalId property

    constructor(libstate) {
        makeAutoObservable(this);
        this._libState = libstate;
    }

    get isSaving() {
        return this._isSaving;
    }
    get intervalId() {
        return this._intervalId;
    }

    setIsSaving(isSaving) {
        this._isSaving = isSaving;
    }

    setIntervalId(intervalId) {
        this._intervalId = intervalId;
    }

    scaleClosets = (scaleFactor, inClosets = null) => {
        const closets = inClosets || this._libState.closetManager.closets;
        closets.forEach((closet) => {
            const polygonCoordinates = closet.coordinates;
            polygonCoordinates.forEach((coordinate) => {
                coordinate.x = coordinate.x * scaleFactor;
                coordinate.y = coordinate.y * scaleFactor;
            });
        });
    };
    scaleAttachments = (scaleFactor) => {
        this._libState.closetManager.attachments.forEach((attachment) => {
            const attachmentCoordinates = attachment.coordinates;
            attachmentCoordinates.forEach((coordinate) => {
                coordinate.x = coordinate.x * scaleFactor;
                coordinate.y = coordinate.y * scaleFactor;
            });
        });
    };
    scaleObstacles = (scaleFactor) => {
        this._libState.closetManager.obstacles.forEach((obstacle) => {
            obstacle.data.coordinate.x *= scaleFactor;
            obstacle.data.coordinate.y *= scaleFactor;
            if (!obstacle.data.radius) {
                obstacle.data.width *= scaleFactor;
                obstacle.data.height *= scaleFactor;
            } else {
                obstacle.data.radius *= scaleFactor;
            }
        });
    };

    scaleInternalWalls = (scaleFactor) => {
        this._libState.closetManager.internalWalls.forEach((wall) => {
            const coordinates = wall.coordinate;
            coordinates.x *= scaleFactor;
            coordinates.y *= scaleFactor;
            wall.setCoordinate(coordinates);
            wall.setLength(wall.length * scaleFactor);
            wall.setDepth(wall.depth * scaleFactor);
        });
    };
    scaleUnits = (scaleFactor) => {
        this._libState.closetManager.units.forEach((unit) => {
            unit.coordinates.forEach((coordinate) => {
                coordinate.x *= scaleFactor;
                coordinate.y *= scaleFactor;
            });
        });
    };

    downloadClosetData = async () => {
        const unitsWithoutClosets = this._libState.closetManager.units.filter(
            (unit) =>
                !this._libState.closetManager.closets.some(
                    (closet) => closet.unitId === unit.id,
                ),
        );

        if (unitsWithoutClosets.length > 0) {
            unitsWithoutClosets.forEach((unit) => {
                message.error(
                    `No closets present in the unit "${unit.name}". Please add a closet first.`,
                );
            });
            return;
        }
        let flag = false;
        this._libState.closetManager.closets.forEach((closet) => {
            if (
                !this._libState.closetManager.attachments?.find(
                    (attachment) =>
                        attachment.closetId === closet.id &&
                        attachment._attachmentType === AttachmentType.DOOR,
                )
            ) {
                message.error(`Please add a door to the ${closet.name}.`);
                flag = true;
            }
        });
        if (flag) return;
        let closets = [];
        let units = [];
        let jsonArrayToDownloadForAllPages = {};
        jsonArrayToDownloadForAllPages['name'] =
            this._libState.viewManager.formData.projectName;
        jsonArrayToDownloadForAllPages['id'] =
            this._libState.viewManager.projectId;
        jsonArrayToDownloadForAllPages['creator'] =
            this._libState.viewManager.formData.projectCreator;
        jsonArrayToDownloadForAllPages['description'] =
            this._libState.viewManager.formData.projectDescription;
        jsonArrayToDownloadForAllPages['projectScope'] =
            this._libState.viewManager.formData.projectScope;
        jsonArrayToDownloadForAllPages['customScope'] =
            this._libState.viewManager.formData.customScope;
        jsonArrayToDownloadForAllPages['color'] =
            this._libState.viewManager.formData.color;
        jsonArrayToDownloadForAllPages['handleStyle'] =
            this._libState.viewManager.formData.handleStyle;
        jsonArrayToDownloadForAllPages['handleColor'] =
            this._libState.viewManager.formData.handleColor;
        jsonArrayToDownloadForAllPages['rodColor'] =
            this._libState.viewManager.formData.rodColor;
        jsonArrayToDownloadForAllPages['doorColor'] =
            this._libState.viewManager.formData.doorColor;
        jsonArrayToDownloadForAllPages['drawerColor'] =
            this._libState.viewManager.formData.drawerColor;
        jsonArrayToDownloadForAllPages['drawerStyle'] =
            this._libState.viewManager.formData.drawerStyle;
        jsonArrayToDownloadForAllPages['doorStyle'] =
            this._libState.viewManager.formData.doorStyle;
        jsonArrayToDownloadForAllPages['legs'] =
            this._libState.viewManager.formData.legs;
        jsonArrayToDownloadForAllPages['shelf'] =
            this._libState.viewManager.formData.shelf;
        jsonArrayToDownloadForAllPages['topDoors'] =
            this._libState.viewManager.formData.topDoors;
        jsonArrayToDownloadForAllPages['bottomDoors'] =
            this._libState.viewManager.formData.bottomDoors;
        jsonArrayToDownloadForAllPages['fileUrl'] =
            this._libState.viewManager.formData.pdfFile.preview;
        jsonArrayToDownloadForAllPages['clientProjectId'] =
            this._libState.apiManager.projectId;
        jsonArrayToDownloadForAllPages['clientParentProjectId'] =
            this._libState.apiManager.parentProjectId;
        const scaleInfo = [];
        this._libState.viewManager.selectedPages.forEach((page) => {
            if (!this._libState.viewManager.doUserHasAccess(page.pageNum))
                return;
            scaleInfo.push({
                pageNo: Number(page.pageNum),
                scale: this._libState.closetManager.getScaleForPage(
                    Number(page.pageNum),
                ),
                type: this._libState.closetManager.getScaleTypeForPage(
                    Number(page.pageNum),
                ),
            });
            let closetDataObj = {};
            let unitDataObj = {};
            let closetData = [];
            let unitData = [];
            const scale =
                this._libState.closetManager.fetchTransformationScaleFactorByPageNo(
                    page.pageNum,
                );
            this.scaleAttachments(1 / scale);
            this.scaleClosets(1 / scale);
            this.scaleObstacles(1 / scale);
            this.scaleUnits(1 / scale);
            this.scaleInternalWalls(1 / scale);
            this._libState.closetManager.closets.forEach((closet) => {
                let attached = [];
                this._libState.closetManager.attachments.forEach(
                    (attachment) => {
                        if (attachment.closetId == closet.id) {
                            attached.push({
                                id: attachment.id,
                                attachmentType: attachment.attachmentType,
                                type: attachment.type,
                                opening: attachment.opening,
                                swing: attachment.swing,
                                points: JSON.parse(
                                    JSON.stringify(attachment.coordinates),
                                ),
                            });
                        }
                    },
                );
                let obstaclle = [];
                this._libState.closetManager.obstacles.forEach((obstacle) => {
                    if (obstacle.closetId == closet.id) {
                        obstaclle.push({
                            id: obstacle.id,
                            type: obstacle.obstacleType,
                            data: JSON.parse(JSON.stringify(obstacle.data)),
                        });
                    }
                });
                let internalWalls = [];
                this._libState.closetManager.internalWalls.forEach((wall) => {
                    if (wall.closetId == closet.id) {
                        internalWalls.push({
                            id: wall.id,
                            coordinates: JSON.parse(
                                JSON.stringify(wall.coordinate),
                            ),
                            length: wall.length,
                            depth: wall.depth,
                            rotation: wall.rotation,
                            position: JSON.parse(
                                JSON.stringify(wall.coordinate),
                            ),
                            normal: JSON.parse(JSON.stringify(wall.normal)),
                            metaData: wall.metaData,
                        });
                    }
                });
                if (closet.pageNo == page.pageNum) {
                    closetData.push({
                        id: closet.id,
                        name: closet.name,
                        scale: this._libState.closetManager.getScaleUnitByClosetId(
                            closet.id,
                        ),
                        unitId: closet.unitId,
                        points: JSON.parse(JSON.stringify(closet.coordinates)),
                        attachments: attached,
                        obstacles: obstaclle,
                        internalWalls: internalWalls,
                    });
                }
            });

            this._libState.closetManager.units.forEach((unit) => {
                if (unit.pageNo != page.pageNum) return;
                unitData.push({
                    id: unit.id,
                    coordinates: JSON.parse(JSON.stringify(unit.coordinates)),
                    scale: this._libState.closetManager.getScaleForUnit(
                        unit.id,
                    ),
                    name: unit.name,
                    qty: unit.qty,
                });
            });
            closetDataObj[page.pageNum] = closetData;
            unitDataObj[page.pageNum] = unitData;
            closets.push(closetDataObj);
            units.push(unitDataObj);

            this.scaleClosets(scale);
            this.scaleAttachments(scale);
            this.scaleObstacles(scale);
            this.scaleUnits(scale);
            this.scaleInternalWalls(scale);
        });
        const matrixData = this._libState.matrixManager.matrixData;
        jsonArrayToDownloadForAllPages['closets'] = closets;
        jsonArrayToDownloadForAllPages['units'] = units;
        jsonArrayToDownloadForAllPages['matrixData'] = matrixData;
        jsonArrayToDownloadForAllPages['scaleInfo'] = scaleInfo;
        const blob = new Blob(
            [JSON.stringify(jsonArrayToDownloadForAllPages)],
            {
                type: 'application/json',
            },
        );
        const res = await this.saveClosetDataBlob(blob);
        return res;
    };

    saveClosetDataBlob = async (blob) => {
        try {
            const { result, response } =
                await this._libState.apiManager.uploadFinalizedProjectData(
                    blob,
                );
            if (!response.ok) {
                message.error(result.message);
                return false;
            } else {
                message.success(result.message);
                return true;
            }
        } catch (error) {
            console.error('Upload failed:', error);
            message.error('Upload failed');
        }
    };
}
